import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Ship, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router'

import { type AWXEntity, AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@ankor-io/ui-components/src/Dialog'
import { Field, Label } from '@ankor-io/ui-components/src/Fieldset'
import { Input } from '@ankor-io/ui-components/src/Input'
import { Radio, RadioField, RadioGroup } from '@ankor-io/ui-components/src/Radio'
import { Select } from '@ankor-io/ui-components/src/Select'

import { useCreateVesselMutation } from '@/api/queries/useVesselQuery'
import { vesselService } from '@/api/services/vessel.service'
import { useUserContext } from '@/providers/user/useUserContext'

const ENTITIES_PER_PAGE = '100'
const SKIP = '0'

type NewVesselModalProps = {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  emitConnectedAccountCreated?: (connectedAccountId: string) => void
}

export const NewVesselModal = ({ isOpen, setIsOpen, emitConnectedAccountCreated }: NewVesselModalProps) => {
  const navigate = useNavigate()
  const createVesselMutation = useCreateVesselMutation()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, updateActiveVesselId, updateActiveConnectedAccountId } = useUserContext()

  const [vesselName, setVesselName] = useState('')
  const [entryType, setEntryType] = useState<'existing' | 'new'>('existing')
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null)
  const [entities, setEntities] = useState<AWXEntity[]>([])

  useEffect(() => {
    if (!isOpen || !activeOrganizationId) {
      return
    }

    const loadEntities = async () => {
      try {
        const queryParams = new URLSearchParams({ take: ENTITIES_PER_PAGE, skip: SKIP })
        const resp = await fetch(`/api/pay/org/${activeOrganizationId}/awx/entities?${queryParams.toString()}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (resp.ok) {
          setEntities(await resp.json())
        }
      } catch (err) {
        console.error('Unable to fetch entities:', err)
      }
    }

    loadEntities()
  }, [isOpen, activeOrganizationId, getAccessToken])

  const resetState = () => {
    setVesselName('')
    setEntryType('existing')
    setSelectedEntityId(null)
  }

  const handleOpenChange = (value: boolean) => {
    setIsOpen(value)
    if (!value) resetState()
  }

  const canCreate = vesselName.trim().length > 0 && (entryType !== 'existing' || selectedEntityId)

  const handleCreate = () => {
    const createVessel = async () => {
      if (!activeOrganizationId) {
        throw new Error('Missing active organization')
      }

      const body = {
        name: vesselName.trim(),
        type: AwxConnectedAccountType.OPERATIONS,
        ...(entryType === 'existing' && selectedEntityId && { entityId: selectedEntityId }),
      }
      const vessel = await createVesselMutation.mutateAsync(body)

      const { vesselAndConnectedAccounts } = await vesselService.getVesselAndConnectedAccounts(
        activeOrganizationId,
        vessel.id,
      )

      const firstAccountId = vesselAndConnectedAccounts?.awxConnectedAccounts?.[0]?.id
      if (!firstAccountId) throw new Error('Vessel created but no connected account found')

      window.dispatchEvent(new CustomEvent('vessel-switching'))
      updateActiveVesselId(vessel.id)
      updateActiveConnectedAccountId(firstAccountId, !!emitConnectedAccountCreated)
      emitConnectedAccountCreated?.(firstAccountId)
      setIsOpen(false)
      navigate('/', { replace: true })
    }

    toast.promise(createVessel(), {
      loading: 'Creating vessel...',
      success: 'Vessel created',
      error: 'Failed to create vessel',
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleOpenChange}
      data-testid="new-vessel-modal"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Ship
            className="size-5 text-gray-500 dark:text-gray-400"
            aria-hidden
          />
          <DialogTitle>Create New Vessel</DialogTitle>
        </div>
        <Button
          plain
          data-testid="close-modal-button"
          onClick={() => handleOpenChange(false)}
        >
          <X data-slot="icon" />
        </Button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Enter the details for your new vessel.</p>
      <DialogBody className="space-y-6">
        <Field>
          <Label htmlFor="vessel-name">Vessel Name</Label>
          <Input
            id="vessel-name"
            placeholder="e.g. My Superyacht"
            value={vesselName}
            onChange={(e) => setVesselName(e.target.value)}
            data-testid="vessel-name-input"
          />
        </Field>
        <Field>
          <Label>Entity Ownership</Label>
          <RadioGroup
            value={entryType}
            onChange={(value) => setEntryType(value as 'existing' | 'new')}
            className="space-y-4"
          >
            <RadioField>
              <Radio
                id="entity-existing"
                value="existing"
              />
              <Label
                htmlFor="entity-existing"
                className="cursor-pointer"
              >
                Select Existing Entity
              </Label>
            </RadioField>
            {entryType === 'existing' && (
              <Select
                value={selectedEntityId ?? ''}
                onChange={(e) => setSelectedEntityId(e.target.value || null)}
                className="w-full mt-2"
                disabled={!entities.length}
                data-testid="entity-select"
              >
                <option value="">Choose entity</option>
                {entities.map((e) => (
                  <option
                    key={e.id}
                    value={e.id}
                  >
                    {e.name}
                  </option>
                ))}
              </Select>
            )}
            <RadioField>
              <Radio
                id="entity-new"
                value="new"
              />
              <Label
                htmlFor="entity-new"
                className="cursor-pointer"
              >
                New Entity
              </Label>
            </RadioField>
          </RadioGroup>
        </Field>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={() => handleOpenChange(false)}
          disabled={createVesselMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleCreate}
          disabled={!canCreate || createVesselMutation.isPending}
          data-testid="create-vessel-button"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewVesselModal
